import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, subirArchivo } from "@/lib/api";
import { useAutosave } from "@/lib/useAutosave";
import { useExpediente } from "@/context/ExpedienteContext";
import { Spinner, AutosaveNote } from "@/components/common";
import { LockBanner } from "@/components/portalCommon";
import { DOC_ESTADO } from "@/constants/labels";
import { Save, Plus, Trash2, UploadCloud, FileText, CheckCircle2, Paperclip, Eye } from "lucide-react";
import DocViewer from "@/components/DocViewer";
import { MotivosCorreccion, VolverACargarBtn } from "@/components/RepetirCarga";

const PARENTESCOS = ["CÓNYUGE / COMPAÑERO(A)", "HIJO(A)", "PADRE", "MADRE", "HERMANO(A)", "OTRO"];
const TIPOS = ["CC", "TI", "CE", "RC", "PA", "OTRO"];
const SOPORTE_CATS = ["Registro civil", "Documento de identidad", "Acta de matrimonio", "Certificado de convivencia", "Otro soporte"];

function SoportesBeneficiario({ benefId, docs, locked, onUploaded, onConfirm }) {
  const [categoria, setCategoria] = useState("");
  const [uploading, setUploading] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const inputRef = useRef();
  const subir = async (file) => {
    if (!file) return;
    if (!categoria) { toast.error("Selecciona el tipo de soporte"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("categoria", categoria); fd.append("beneficiario_id", benefId); fd.append("file", file);
      await subirArchivo("/portal/documentos", fd);
      toast.success("Soporte cargado (Recibido)"); setCategoria(""); onUploaded();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Paperclip className="h-3.5 w-3.5" /> Soportes del beneficiario</p>
      {docs.length === 0 && <p className="mb-2 text-xs text-slate-400">Aún no has adjuntado soportes para este beneficiario.</p>}
      {docs.length > 0 && (
        <div className="mb-2 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {docs.map((d) => {
            const est = DOC_ESTADO[d.estado] || DOC_ESTADO.recibido;
            return (
              <div key={d.id} className="flex flex-wrap items-center gap-2 p-2.5">
                <FileText className="h-4 w-4 text-vino-700" />
                <div className="min-w-0 flex-1"><p className="truncate text-xs font-medium text-slate-800">{d.categoria}</p><p className="truncate text-[11px] text-slate-400">{d.original_filename}</p></div>
                <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${est.cls}`}>{est.label}</span>
                <button onClick={() => setViewDoc(d)} data-testid={`benef-soporte-ver-${d.id}`} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-medium text-vino-700 hover:bg-vino-50"><Eye className="h-3 w-3" /> Ver</button>
                {!locked && d.estado === "recibido" && (
                  <button onClick={() => onConfirm(d.id)} data-testid={`benef-soporte-confirmar-${d.id}`} className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-medium text-vino-700 hover:bg-vino-50"><CheckCircle2 className="h-3 w-3" /> Confirmar</button>
                )}
                {d.estado === "correccion_requerida" && <VolverACargarBtn doc={d} onDone={onUploaded} small />}
                {d.estado === "correccion_requerida" && <MotivosCorreccion c={d.correccion} compact />}
              </div>
            );
          })}
        </div>
      )}
      {!locked && (
        <div className="flex flex-wrap items-center gap-2">
          <select value={categoria} onChange={(e) => setCategoria(e.target.value)} data-testid={`benef-soporte-cat-${benefId}`} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs outline-none focus:border-vino-500">
            <option value="">Tipo de soporte</option>
            {SOPORTE_CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`benef-soporte-subir-${benefId}`} className="flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-xs font-medium text-vino-700 hover:bg-vino-50 disabled:opacity-60">
            {uploading ? <Spinner /> : <UploadCloud className="h-3.5 w-3.5" />} Adjuntar soporte
          </button>
          <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { subir(e.target.files[0]); e.target.value = ""; }} />
        </div>
      )}
      {viewDoc && (
        <DocViewer
          url={`/portal/documentos/${viewDoc.id}/download`}
          titulo={viewDoc.categoria}
          subtitulo={viewDoc.original_filename}
          nombreArchivo={viewDoc.original_filename}
          onClose={() => setViewDoc(null)}
        />
      )}
    </div>
  );
}

export default function Beneficiarios() {
  const { exp, reload } = useExpediente();
  const [incluye, setIncluye] = useState(null);
  const [lista, setLista] = useState([]);
  const [saving, setSaving] = useState(false);

  const { auto: autoBenef, markSaved: markBenefSaved } = useAutosave({
    data: { incluye, lista: incluye ? lista : [] },
    enabled: !!exp && !exp.bloqueado,
    save: async (d) => {
      const r = await api.put("/portal/beneficiarios", d);
      // Completa los ids asignados por el servidor sin interrumpir la digitación.
      const srv = r.data?.beneficiarios?.lista || [];
      setLista((prev) => prev.map((row, i) => (row.id || !srv[i] ? row : { ...row, id: srv[i].id })));
    },
  });

  useEffect(() => {
    if (exp) {
      const inc = exp.beneficiarios?.incluye ?? null;
      const lst = exp.beneficiarios?.lista || [];
      setIncluye(inc);
      setLista(lst);
      markBenefSaved({ incluye: inc, lista: inc ? lst : [] });
    }
  }, [exp]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!exp) return <div className="flex h-64 items-center justify-center text-vino-700"><Spinner className="h-6 w-6" /></div>;
  const locked = exp.bloqueado;

  const setRow = (i, k, v) => setLista((p) => { const a = [...p]; a[i] = { ...a[i], [k]: v }; return a; });
  const add = () => setLista((p) => [...p, { nombre: "", parentesco: "", fecha_nacimiento: "", tipo_documento: "", numero_documento: "", incluir_eps: false, incluir_caja: false }]);
  const del = (i) => setLista((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/portal/beneficiarios", { incluye, lista: incluye ? lista : [] });
      markBenefSaved();
      toast.success("Beneficiarios guardados"); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmarSoporte = async (id) => {
    try { await api.patch(`/portal/documentos/${id}/confirmar`); toast.success("Soporte confirmado"); reload(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const docsDe = (benefId) => (exp.documentos || []).filter((d) => d.beneficiario_id === benefId);

  const inp = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-vino-500 disabled:bg-slate-50";

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Mis beneficiarios</h1>
        <p className="mt-1 text-sm text-slate-500">Indica si deseas incluir beneficiarios. HUMAN validará los soportes y la inclusión.</p>
      </div>
      {locked && <LockBanner />}

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="mb-3 text-sm font-medium text-slate-700">¿Deseas incluir beneficiarios?</p>
        <div className="flex gap-6">
          {[{ v: true, l: "Sí" }, { v: false, l: "No" }].map((o) => (
            <label key={o.l} className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="radio" disabled={locked} name="incluye" checked={incluye === o.v} onChange={() => setIncluye(o.v)} data-testid={`benef-${o.l.toLowerCase()}`} className="h-4 w-4 accent-vino-700" />
              {o.l}
            </label>
          ))}
        </div>
      </div>

      {incluye && (
        <div className="space-y-4">
          {lista.length === 0 && <p className="text-sm text-slate-400">Agrega al menos un beneficiario.</p>}
          {lista.map((b, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Beneficiario {i + 1}</span>
                {!locked && <button onClick={() => del(i)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="lg:col-span-2"><label className="mb-1 block text-xs font-medium text-slate-500">Nombre completo</label>
                  <input disabled={locked} className={inp} value={b.nombre || ""} onChange={(e) => setRow(i, "nombre", e.target.value.toUpperCase())} data-testid={`benef-nombre-${i}`} /></div>
                <div><label className="mb-1 block text-xs font-medium text-slate-500">Parentesco</label>
                  <select disabled={locked} className={inp} value={b.parentesco || ""} onChange={(e) => setRow(i, "parentesco", e.target.value)}><option value="">Selecciona</option>{PARENTESCOS.map((p) => <option key={p}>{p}</option>)}</select></div>
                <div><label className="mb-1 block text-xs font-medium text-slate-500">Fecha de nacimiento</label>
                  <input type="date" disabled={locked} className={inp} value={b.fecha_nacimiento || ""} onChange={(e) => setRow(i, "fecha_nacimiento", e.target.value)} data-testid={`benef-fnac-${i}`} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="mb-1 block text-xs font-medium text-slate-500">Tipo doc.</label>
                    <select disabled={locked} className={inp} value={b.tipo_documento || ""} onChange={(e) => setRow(i, "tipo_documento", e.target.value)}><option value="">—</option>{TIPOS.map((t) => <option key={t}>{t}</option>)}</select></div>
                  <div><label className="mb-1 block text-xs font-medium text-slate-500">Número</label>
                    <input disabled={locked} className={inp} value={b.numero_documento || ""} onChange={(e) => setRow(i, "numero_documento", e.target.value)} /></div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-5">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={locked} checked={!!b.incluir_eps} onChange={(e) => setRow(i, "incluir_eps", e.target.checked)} className="h-4 w-4 accent-vino-700" /> Incluir en EPS</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={locked} checked={!!b.incluir_caja} onChange={(e) => setRow(i, "incluir_caja", e.target.checked)} className="h-4 w-4 accent-vino-700" /> Incluir en caja</label>
              </div>
              {b.id ? (
                <SoportesBeneficiario benefId={b.id} docs={docsDe(b.id)} locked={locked} onUploaded={reload} onConfirm={confirmarSoporte} />
              ) : (
                !locked && <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">Guarda los beneficiarios para poder adjuntar sus soportes.</p>
              )}
            </div>
          ))}
          {!locked && <button onClick={add} data-testid="benef-add" className="flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2 text-sm font-medium text-vino-700 hover:bg-vino-50"><Plus className="h-4 w-4" /> Agregar beneficiario</button>}
        </div>
      )}

      {!locked && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          <AutosaveNote auto={autoBenef} testid="benef-autosave-status" />
          <button onClick={save} disabled={saving || incluye === null} data-testid="benef-guardar" className="flex items-center gap-2 rounded-lg bg-vino-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-60">
            {saving ? <Spinner /> : <Save className="h-4 w-4" />} Guardar
          </button>
        </div>
      )}
    </div>
  );
}
